import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Spin, Tooltip, Space, Tabs } from 'antd'
import Button from '../../../../core/components/Button.jsx'
import { RecordFormDeleteEllipses } from '../../../../core/components/RecordFormDeleteEllipses.tsx'
import { AgentProvenanceTag } from '../../../../core/components/AgentProvenanceTag.tsx'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { fetchFundraisingById, deleteProspect } from '../../services/fundraisingService.ts'
import { updateProspectRecord, removeProspectFromList } from '../../../../state_manager/state_update_actions/fundraising/index.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { selectProspectById } from '../../../../state_manager/state_update_actions/fundraising/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  updateActiveRecord,
  removeActiveRecord
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import { DashboardTab } from './tabs/DashboardTab.tsx'
import { RelationshipTab } from './tabs/RelationshipTab.tsx'
import { ChangeLogTab } from '../../../change_log/components/ChangeLogTab.tsx'
import { AgentTab } from '../../../internal_agents/components/AgentTab.tsx'
import { ProspectFields } from '../../utils/fieldConfigs.ts'
import { AccountRecordFields } from '../../../../state_manager/data_models/account_record.ts'
import { ProspectRecordFields } from '../../../../state_manager/data_models/prospect_record.ts'
import { CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import { RELATIONSHIP_CATEGORY_FOUNDER } from '../../../../state_manager/globals.ts'
import { UNKNOWN_CONTACT, CONFIRM_DELETE_PROSPECT_TITLE } from '../../../../state_manager/constants/strings.ts'
import { removeTab, selectActiveTabKey, updateTabPath } from '../../../../store/slices/tabSlice'
import { stripTrailingFormRouteSegments } from '../../../../core/contexts/DrawerContext.jsx'

export default function FullProspectForm({
  recordId: propRecordId,
  isNew: propIsNew
}: {
  recordId?: string | null
  isNew?: boolean
}) {
  const { urlRecordId } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new'

  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])
  const fundraisingState = useSelector((state: any) => state.fundraising)
  const pipelineConfigs = useSelector((state: any) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: any) => state.globalConfigs?.pipeline_stages || [])

  const prospectFromState = useSelector((state: any) =>
    recordId && !isNew ? selectProspectById(state, recordId) : null
  )
  const prospectFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )
  const currentProspect = prospectFromState || prospectFromActiveRecords

  const activeTabKey = useSelector((state: any) => selectActiveTabKey(state))

  const [companyTeamMembers, setCompanyTeamMembers] = useState<any[]>([])
  const [isFetchingProspect, setIsFetchingProspect] = useState(false)
  const [isDeletingProspect, setIsDeletingProspect] = useState(false)
  const hasAttemptedFetch = useRef(false)
  const fundraisingStateRef = useRef(fundraisingState)
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAgentTabRef = useRef<(() => Promise<void>) | null>(null)
  const refreshProspectStatesRef = useRef<(() => Promise<void>) | null>(null)
  const refreshRelationshipTabRef = useRef<(() => Promise<void>) | null>(null)

  // Controlled tabs so the Dashboard's signals strip can send the user to the
  // Agent tab ("View more"), the same contract the deal and company forms use.
  const agentTabKey = `agent-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  // Deep-link tab target (?tab=agent|relationship|dashboard), the contract the
  // deal and company forms honour. Agent citations arrive as
  // ?tab=dashboard&interaction=<id> — the timeline is on the Dashboard.
  const resolveTabFromQuery = () => {
    if (isNew) return 'dashboard'
    switch (new URLSearchParams(window.location.search).get('tab')) {
      case 'agent':
        return agentTabKey
      case 'relationship':
        return 'relationship'
      default:
        return 'dashboard'
    }
  }
  const [activeTab, setActiveTab] = useState<string>(resolveTabFromQuery)
  const location = useLocation()

  useEffect(() => {
    if (isNew || !recordId) return
    if (!location.pathname.endsWith(`/forms/full/${recordId}`)) return
    if (!new URLSearchParams(location.search).get('tab')) return
    setActiveTab(resolveTabFromQuery())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  const companyTeamDataKey = useMemo(() => {
    const team = currentProspect?.['prog__company_team']
    if (!team || !Array.isArray(team)) {
      return team === undefined ? undefined : '[]'
    }
    if (team.length === 0) {
      return '[]'
    }
    return JSON.stringify(
      team.map((m: any) => ({
        id: m[CompanyTeamEntryFields.contact_accounts__id],
        contactId: m[CompanyTeamEntryFields.contacts__id],
        founder: m[CompanyTeamEntryFields.prog__founder],
        current: m[CompanyTeamEntryFields.contact_accounts__current]
      }))
    )
  }, [currentProspect?.['prog__company_team']])

  useEffect(() => {
    fundraisingStateRef.current = fundraisingState
  }, [fundraisingState])

  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordId, dispatch])

  const previousRecordIdRef = useRef<string | null | undefined>(null)

  useEffect(() => {
    const recordIdString = recordId?.toString()
    const previousRecordIdString = previousRecordIdRef.current?.toString()

    if (!isNew && recordIdString && recordIdString !== previousRecordIdString) {
      const currentProspectNow = prospectFromActiveRecords || prospectFromState

      if (currentProspectNow) {
        hasAttemptedFetch.current = true
        dispatch(setActiveRecord({ uuid: recordIdString, record: currentProspectNow }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
      } else if (!hasAttemptedFetch.current) {
        const currentToken = getAccessToken(store.getState().auth)
        if (currentToken) {
          hasAttemptedFetch.current = true
          setIsFetchingProspect(true)
          fetchFundraisingById(recordIdString, currentToken)
            .then((response) => {
              if (response.data && !response.error) {
                const fetchedProspect = response.data
                dispatch(setActiveRecord({ uuid: recordIdString, record: fetchedProspect }))
                dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
              } else if (response.error) {
                console.error('Error fetching prospect:', response.error)
              }
            })
            .catch((error) => {
              console.error('Error fetching prospect:', error)
            })
            .finally(() => {
              setIsFetchingProspect(false)
            })
        }
      }
      previousRecordIdRef.current = recordId
    }
  }, [recordId, isNew, dispatch, prospectFromActiveRecords, prospectFromState])

  useEffect(() => {
    return () => {
      if (recordId && !isNew) {
        const prospectIdString = recordId.toString()
        dispatch(unregisterRecordUsage({ uuid: prospectIdString, context: 'fullForm' }))
      }
    }
  }, [recordId, isNew, dispatch])

  useEffect(() => {
    if (currentProspect && !isNew) {
      const companyTeamData = currentProspect['prog__company_team']
      if (companyTeamData && Array.isArray(companyTeamData)) {
        const contactRelationships: Record<string, any> = {}

        companyTeamData.forEach((member: any) => {
          const contactId = member[CompanyTeamEntryFields.contacts__id]
          const isFounder = member[CompanyTeamEntryFields.prog__founder] || false
          const relationshipId = member[CompanyTeamEntryFields.contact_accounts__id]

          const contactData = {
            display_name: member[CompanyTeamEntryFields.prog__display_name],
            first_name: member[CompanyTeamEntryFields.contacts__first_name],
            last_name: member[CompanyTeamEntryFields.contacts__last_name],
            email: member[CompanyTeamEntryFields.contacts__email],
            mobile_phone: member[CompanyTeamEntryFields.contacts__mobile_phone],
            preferred_name: member[CompanyTeamEntryFields.contacts__preferred_name]
          }

          if (!contactRelationships[contactId]) {
            const relationshipCategoryId = member[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
            const category =
              relationshipCategoryId && relationshipCategoryId !== RELATIONSHIP_CATEGORY_FOUNDER
                ? relationshipCategoryId
                : null

            contactRelationships[contactId] = {
              key: relationshipId || `team_${contactId}`,
              employeeRelationshipId: !isFounder ? relationshipId : null,
              founderRelationshipId: isFounder ? relationshipId : null,
              contact: {
                value: contactId,
                label:
                  contactData.display_name ||
                  `${contactData.first_name || ''} ${contactData.last_name || ''}`.trim() ||
                  contactData.email ||
                  UNKNOWN_CONTACT,
                data: contactData
              },
              founder: isFounder,
              category: category,
              jobTitle: member[CompanyTeamEntryFields.contact_accounts__title] || '',
              email: contactData.email || '',
              contactNumber: contactData.mobile_phone || ''
            }
          } else {
            if (!isFounder) {
              contactRelationships[contactId].key = relationshipId
              contactRelationships[contactId].employeeRelationshipId = relationshipId
              const relationshipCategoryId = member[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
              contactRelationships[contactId].category =
                relationshipCategoryId && relationshipCategoryId !== RELATIONSHIP_CATEGORY_FOUNDER
                  ? relationshipCategoryId
                  : null
              contactRelationships[contactId].jobTitle = member[CompanyTeamEntryFields.contact_accounts__title] || ''
            } else {
              contactRelationships[contactId].founderRelationshipId = relationshipId
              contactRelationships[contactId].founder = true
              const relationshipCategoryId = member[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
              if (relationshipCategoryId && relationshipCategoryId !== RELATIONSHIP_CATEGORY_FOUNDER) {
                contactRelationships[contactId].category = relationshipCategoryId
              } else if (!contactRelationships[contactId].category) {
                contactRelationships[contactId].category = null
              }
            }
          }
        })

        const teamMembers = Object.values(contactRelationships)

        const filteredTeamMembers = teamMembers.filter((member) => {
          return member.employeeRelationshipId !== null && member.employeeRelationshipId !== undefined
        })

        setCompanyTeamMembers(filteredTeamMembers)
      } else {
        setCompanyTeamMembers([])
      }
    } else if (isNew) {
      setCompanyTeamMembers([])
    }
  }, [currentProspect, companyTeamDataKey, isNew])

  const companyTeamMembersKey = useMemo(() => {
    if (!companyTeamMembers || !Array.isArray(companyTeamMembers) || companyTeamMembers.length === 0) {
      return companyTeamMembers === undefined ? undefined : '[]'
    }
    return JSON.stringify(
      companyTeamMembers.map((m: any) => ({
        key: m.key,
        founder: m.founder,
        contactId: m.contact?.value,
        category: m.category
      }))
    )
  }, [companyTeamMembers])

  const handlePatchSuccess = useCallback(
    async (patchRecordId: string, updates: Record<string, any>) => {
      if (!patchRecordId) return

      const isAccountUpdate = patchRecordId !== recordId && recordId
      const prospectId = isAccountUpdate ? recordId : patchRecordId

      const investorUpdated =
        updates[ProspectFields.prospects__investor] !== undefined || updates.investor !== undefined

      const currentToken = getAccessToken(store.getState().auth)
      if (investorUpdated && prospectId && currentToken && !isNew) {
        try {
          const response = await fetchFundraisingById(prospectId, currentToken)
          if (response.data && !response.error) {
            dispatch(updateProspectRecord({ prospectId: prospectId.toString(), updates: response.data }))
            dispatch(updateActiveRecord({ uuid: prospectId.toString(), updates: response.data }))
            return
          }
        } catch (error) {
          console.error('Error fetching prospect after Investor update:', error)
        }
      }

      if (updates[ProspectFields.pipeline_prospects__stage]) {
        const pipelineConfigId = updates[ProspectFields.pipeline_prospects__stage]
        const config = pipelineConfigs.find((c: any) => c.id === pipelineConfigId)
        if (config) {
          const stage = pipelineStages.find((s: any) => s.id === config.stage)
          if (stage && stage.name) {
            updates[ProspectFields.pipeline_prospects__stage__label] = stage.name
          }
        }
      }

      if (updates[AccountRecordFields.accounts__business_country_code] && countriesData.length > 0) {
        const countryCode = updates[AccountRecordFields.accounts__business_country_code]
        const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
        if (countryLabel) {
          updates[ProspectRecordFields.accounts__business_country_code__label] = countryLabel
          if (updates[AccountRecordFields.accounts__business_country_label] === undefined) {
            updates[AccountRecordFields.accounts__business_country_label] = countryLabel
          }
        }
      }

      const { id: accountId, ...prospectUpdates } = updates

      if (prospectId) {
        if (Object.keys(prospectUpdates).length > 0) {
          dispatch(updateProspectRecord({ prospectId, updates: prospectUpdates }))

          dispatch(updateActiveRecord({ uuid: prospectId, updates: prospectUpdates }))
        }
      }

    },
    [dispatch, countriesData, pipelineConfigs, pipelineStages, recordId, isNew]
  )

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      const updates: Record<string, any> = {}

      if (
        field === AccountRecordFields.accounts__business_city_code ||
        field === ProspectRecordFields.accounts__business_city_code__label
      ) {
        const countryCode = value?.data?.countryCode || value?.countryCode
        const cityLabel = value?.name || value?.label || ''
        const cityCode = typeof value === 'object' ? value?.value || value?.code : value

        if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
          if (typeof value === 'object' && value !== null) {
            updates[ProspectRecordFields.accounts__business_city_code__label] = value
          } else {
            const cityObject = {
              value: cityCode,
              name: cityLabel || `City ${cityCode}`,
              label: cityLabel || `City ${cityCode}`,
              data: { countryCode }
            }
            updates[ProspectRecordFields.accounts__business_city_code__label] = cityObject
          }
        }

        if (countryCode) {
          updates[AccountRecordFields.accounts__business_country_code] = countryCode
          if (countriesData.length > 0) {
            const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
            if (countryLabel) {
              updates[ProspectRecordFields.accounts__business_country_code__label] = countryLabel
            } else {
              updates[ProspectRecordFields.accounts__business_country_code__label] = countryCode
            }
          } else {
            updates[ProspectRecordFields.accounts__business_country_code__label] = countryCode
          }
        }
      }

      if (
        field === AccountRecordFields.accounts__business_country_code ||
        field === ProspectRecordFields.accounts__business_country_code__label
      ) {
        const countryCode = value?.value || value
        const countryLabel = value?.label || value

        updates[AccountRecordFields.accounts__business_city_code] = null
        updates[ProspectRecordFields.accounts__business_city_code__label] = null

        if (countryCode) {
          updates[AccountRecordFields.accounts__business_country_code] = countryCode
        }
        if (countryLabel && countryLabel !== countryCode) {
          updates[ProspectRecordFields.accounts__business_country_code__label] = countryLabel
        } else if (countriesData.length > 0 && countryCode) {
          const countryName = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryName) {
            updates[ProspectRecordFields.accounts__business_country_code__label] = countryName
          } else {
            updates[ProspectRecordFields.accounts__business_country_code__label] = countryCode
          }
        } else if (countryCode) {
          updates[ProspectRecordFields.accounts__business_country_code__label] = countryCode
        }
      }

      if (Object.keys(updates).length > 0 && !isNew && recordId && currentProspect) {
        dispatch(updateProspectRecord({ prospectId: recordId, updates }))

        dispatch(updateActiveRecord({ uuid: recordId, updates }))
      }
    },
    [currentProspect, recordId, isNew, dispatch, countriesData]
  )

  const handleDeleteProspect = useCallback(async () => {
    if (!recordId || isNew) return
    const token = getAccessToken(store.getState().auth)
    if (!token) {
      showErrorToast('Not authenticated')
      return
    }
    setIsDeletingProspect(true)
    try {
      await deleteProspect(recordId, token)
      toast.success('Record deleted')
      const id = String(recordId)
      dispatch(removeProspectFromList({ prospectId: id }))
      dispatch(removeActiveRecord({ uuid: id }))
      dispatch(unregisterRecordUsage({ uuid: id, context: 'fullForm' }))
      window.isClosingTab = true
      dispatch(removeTab({ key: activeTabKey }))
      const nextState = store.getState().tabs
      const nextTab = nextState.tabs.find((t: { key: string }) => t.key === nextState.activeTabKey)
      if (nextTab?.path) {
        const pathWithoutFormShell = stripTrailingFormRouteSegments(nextTab.path)
        if (pathWithoutFormShell !== nextTab.path) {
          dispatch(updateTabPath({ key: nextTab.key, path: pathWithoutFormShell }))
        }
        navigate(pathWithoutFormShell, { replace: true })
      }
      setTimeout(() => {
        window.isClosingTab = false
      }, 150)
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to delete prospect')
    } finally {
      setIsDeletingProspect(false)
    }
  }, [recordId, isNew, dispatch, navigate, activeTabKey])

  const handleRefresh = useCallback(async () => {
    if (!recordId || isNew) {
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (!currentToken) {
      return
    }

    setIsFetchingProspect(true)
    try {
      const result = await fetchFundraisingById(recordId, currentToken)
      if (result && result.data) {
        const recordIdString = recordId.toString()
        dispatch(setActiveRecord({ uuid: recordIdString, record: result.data }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
        dispatch(updateProspectRecord({ prospectId: recordIdString, updates: result.data }))
        toast.success('Refreshed Successfully')

        if (refreshInteractionsRef.current) {
          await refreshInteractionsRef.current()
        }

        if (refreshTasksRef.current) {
          await refreshTasksRef.current()
        }

        if (refreshAgentTabRef.current) {
          await refreshAgentTabRef.current()
        }

        if (refreshProspectStatesRef.current) {
          await refreshProspectStatesRef.current()
        }

        if (refreshRelationshipTabRef.current) {
          await refreshRelationshipTabRef.current()
        }
      }
    } catch (error) {
      console.error('Error refreshing prospect:', error)
    } finally {
      setIsFetchingProspect(false)
    }
  }, [recordId, isNew, dispatch])

  const handleCompanyTeamMembersChange = useCallback(
    (updatedCompanyTeamMembers: any[]) => {
      setCompanyTeamMembers(updatedCompanyTeamMembers)
    },
    [recordId, isNew, dispatch]
  )

  const shouldShowLoading = !isNew && !currentProspect && isFetchingProspect

  if (shouldShowLoading) {
    return (
      <div className='h-full'>
        <div className='w-full bg-gray-50 p-3'>
          <div className='mx-auto' style={{ maxWidth: 'calc(100vw - 24px)' }}>
            <div className='flex items-center justify-center min-h-[400px]'>
              <Spin size='large' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        data-debug='full-form-container'
        data-component='FullProspectForm'
        data-record-id={recordId}
        data-is-new={isNew}
        className='h-full w-full min-h-0 flex flex-col'
      >
        {!isNew && (
          <div className='flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0'>
            <div className='min-w-0'>
              <AgentProvenanceTag provenance={currentProspect?.[ProspectRecordFields.prog__created_provenance]} />
            </div>
            <Space size='small'>
              <RecordFormDeleteEllipses
                confirmTitle={CONFIRM_DELETE_PROSPECT_TITLE}
                onDelete={handleDeleteProspect}
                loading={isDeletingProspect}
                disabled={isFetchingProspect}
              />
              <Tooltip title='Refresh'>
                <Button
                  variant='default'
                  data-debug='refresh-full-form-btn'
                  size='small'
                  icon={<FontAwesomeIcon icon={faArrowsRotate} style={{ color: '#9ca3af' }} />}
                  onClick={handleRefresh}
                  loading={isFetchingProspect}
                  disabled={isDeletingProspect}
                  style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
                />
              </Tooltip>
            </Space>
          </div>
        )}
        <div data-debug='full-form-wrapper' className='flex-1 w-full h-full bg-gray-50 p-3 overflow-hidden'>
          <div data-debug='full-form-content' className='flex-1 flex-col w-full h-full'>
            {!isNew && recordId ? (
              <Tabs
                type='line'
                size='middle'
                className='deal-form-tabs h-full'
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: 'dashboard',
                    label: 'Dashboard',
                    children: (
                      <div data-debug='full-form-dashboard-tab' className='h-full overflow-y-auto overflow-x-hidden'>
                        <DashboardTab
                          prospectRecord={currentProspect}
                          handleFieldChange={handleFieldChange}
                          onPatchSuccess={handlePatchSuccess}
                          recordId={recordId}
                          companyTeamMembers={companyTeamMembers}
                          companyTeamMembersKey={companyTeamMembersKey}
                          onCompanyTeamMembersChange={handleCompanyTeamMembersChange}
                          onRefreshInteractionsReady={(refreshFn) => {
                            refreshInteractionsRef.current = refreshFn
                          }}
                          onRefreshTasksReady={(refreshFn) => {
                            refreshTasksRef.current = refreshFn
                          }}
                          onRefreshProspectStatesReady={(refreshFn) => {
                            refreshProspectStatesRef.current = refreshFn
                          }}
                          onNavigateToAgentTab={() => setActiveTab(agentTabKey)}
                        />
                      </div>
                    )
                  },
                  {
                    key: 'relationship',
                    label: 'Relationship',
                    children: (
                      <div data-debug='full-form-relationship-tab' className='h-full overflow-y-auto overflow-x-hidden'>
                        <RelationshipTab
                          prospectRecord={currentProspect}
                          recordId={recordId}
                          onRefreshReady={(refreshFn) => {
                            refreshRelationshipTabRef.current = refreshFn
                          }}
                        />
                      </div>
                    )
                  },
                  {
                    key: agentTabKey,
                    label: 'Agent',
                    children: (
                      <div data-debug='full-form-agent-tab' className='h-full overflow-y-auto'>
                        <AgentTab
                          key={agentTabKey}
                          subjectTable='prospects'
                          subjectId={recordId}
                          agentTypeKey='prospect_tracker'
                          onRefreshReady={(refreshFn) => {
                            refreshAgentTabRef.current = refreshFn
                          }}
                        />
                      </div>
                    )
                  },
                  {
                    key: 'change-log',
                    label: 'Change Log',
                    children: (
                      <div data-debug='full-form-change-log-tab' className='h-full'>
                        <ChangeLogTab
                          key={`change-log-${recordId}`}
                          contextTable='prospects'
                          recordId={recordId}
                        />
                      </div>
                    )
                  }
                ]}
              />
            ) : (
              <DashboardTab
                prospectRecord={currentProspect}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={handlePatchSuccess}
                recordId={recordId}
                companyTeamMembers={companyTeamMembers}
                companyTeamMembersKey={companyTeamMembersKey}
                onCompanyTeamMembersChange={handleCompanyTeamMembersChange}
                onRefreshInteractionsReady={(refreshFn) => {
                  refreshInteractionsRef.current = refreshFn
                }}
                onRefreshTasksReady={(refreshFn) => {
                  refreshTasksRef.current = refreshFn
                }}
              />
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  )
}
